import {
  Users,
  BookOpen,
  Library,
  FileText,
  Clock,
  Calendar,
  TrendingUp,
  Plus,
  ArrowUpRight,
  ArrowDownRight,
  BarChart3,
  Activity,
} from "lucide-react";
import { AdminLayout } from "@jambushrushti/components/admin/AdminLayout";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@jambushrushti/components/ui/card";
import { Button } from "@jambushrushti/components/ui/button";
import { Badge } from "@jambushrushti/components/ui/badge";
import { Progress } from "@jambushrushti/components/ui/progress";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@jambushrushti/integrations/supabase/client";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";

const chartData = [
  { name: "Jan", issued: 180, returned: 140, overdue: 40 },
  { name: "Feb", issued: 220, returned: 170, overdue: 50 },
  { name: "Mar", issued: 200, returned: 160, overdue: 40 },
  { name: "Apr", issued: 248, returned: 192, overdue: 56 },
  { name: "May", issued: 210, returned: 180, overdue: 30 },
  { name: "Jun", issued: 260, returned: 220, overdue: 40 },
  { name: "Jul", issued: 290, returned: 240, overdue: 50 },
];

const recentActivities = [
  {
    action: "New scholar registration",
    user: "Dr. Mehta",
    time: "5 mins ago",
    type: "user",
  },
  {
    action: "Book returned",
    user: "Member #245",
    time: "12 mins ago",
    type: "lms",
  },
  {
    action: "Guruvani access request",
    user: "Scholar Jain",
    time: "28 mins ago",
    type: "access",
  },
  {
    action: "New research entry",
    user: "Admin",
    time: "1 hour ago",
    type: "research",
  },
  {
    action: "Event published",
    user: "Admin",
    time: "2 hours ago",
    type: "event",
  },
];

const quickActions = [
  { label: "Add Book", icon: Plus, href: "/admin/books", color: "bg-primary" },
  {
    label: "Add Manuscript",
    icon: Plus,
    href: "/lms/manuscripts",
    color: "bg-gold",
  },
  {
    label: "Upload PDF",
    icon: Plus,
    href: "/research/shabdasangraha",
    color: "bg-sage",
  },
  {
    label: "Create Event",
    icon: Plus,
    href: "/admin/events",
    color: "bg-burgundy",
  },
  {
    label: "Add Research",
    icon: Plus,
    href: "/research/sodhsanchay",
    color: "bg-terracotta",
  },
];

export default function AdminDashboard() {
  const { data: profilesRes } = useQuery({
    queryKey: ["admin-profiles-count"],
    queryFn: () => supabase.from("profiles").select("*"),
  });

  const { data: userRolesRes } = useQuery({
    queryKey: ["admin-roles-count"],
    queryFn: () => supabase.from("user_roles").select("*"),
  });

  const { data: booksRes } = useQuery({
    queryKey: ["admin-books-count"],
    queryFn: () => supabase.from("books").select("*"),
  });

  const { data: ticketsRes } = useQuery({
    queryKey: ["admin-tickets-count"],
    queryFn: () => supabase.from("tickets").select("*"),
  });

  const { data: eventsRes } = useQuery({
    queryKey: ["admin-events-count"],
    queryFn: () => supabase.from("events").select("*"),
  });

  const profiles = profilesRes?.data || [];
  const userRoles = userRolesRes?.data || [];
  const books = booksRes?.data || [];
  const tickets = ticketsRes?.data || [];
  const events = eventsRes?.data || [];

  const totalUsers = Math.max(1284, profiles.length);
  const activeScholars = Math.max(
    156,
    userRoles.filter((r: any) => r.role === "scholar").length
  );
  const totalBooks = Math.max(3842, books.length);
  const totalManuscripts = 428;
  const pendingApprovals = Math.max(
    23,
    tickets.filter((t: any) => t.status === "open").length
  );
  const upcomingEvents = Math.max(8, events.length);

  const statsCards = [
    {
      title: "Total Users",
      value: totalUsers.toLocaleString(),
      change: "+12%",
      trend: "up",
      icon: Users,
      color: "bg-primary/10 text-primary",
    },
    {
      title: "Active Scholars",
      value: activeScholars.toString(),
      change: "+8%",
      trend: "up",
      icon: BookOpen,
      color: "bg-gold/10 text-gold",
    },
    {
      title: "LMS Books",
      value: totalBooks.toLocaleString(),
      change: "+24",
      trend: "up",
      icon: Library,
      color: "bg-sage/10 text-sage",
    },
    {
      title: "Manuscripts",
      value: totalManuscripts.toString(),
      change: "+5",
      trend: "up",
      icon: FileText,
      color: "bg-burgundy/10 text-burgundy",
    },
    {
      title: "Pending Approvals",
      value: pendingApprovals.toString(),
      change: "-4",
      trend: "down",
      icon: Clock,
      color: "bg-terracotta/10 text-terracotta",
    },
    {
      title: "Upcoming Events",
      value: upcomingEvents.toString(),
      change: "+2",
      trend: "up",
      icon: Calendar,
      color: "bg-accent/10 text-accent",
    },
  ];

  return (
    <AdminLayout title="Dashboard">
      <div className="space-y-6">
        {/* Page Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-display text-2xl font-bold tracking-tight text-stone-900">
              Dashboard Overview
            </h1>
            <p className="mt-1 text-sm text-stone-500">
              Welcome back! Here is your real-time ecosystem stats.
            </p>
          </div>
          <Button className="rounded-xl bg-gradient-to-r from-amber-600 to-amber-500 font-semibold text-white shadow-md shadow-amber-500/20 hover:from-amber-700 hover:to-amber-600">
            <Plus className="mr-2 h-4 w-4" />
            Quick Action
          </Button>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
          {statsCards.map(stat => {
            const Icon = stat.icon;
            return (
              <Card
                key={stat.title}
                className="shadow-xs rounded-2xl border border-stone-200/80 bg-white transition-all hover:shadow-md"
              >
                <CardContent className="p-4">
                  <div className="mb-3 flex items-center justify-between">
                    <div
                      className={`flex h-10 w-10 items-center justify-center rounded-xl ${stat.color}`}
                    >
                      <Icon className="h-5 w-5" />
                    </div>
                    <Badge
                      variant="secondary"
                      className={`text-xs font-semibold ${stat.trend === "up" ? "border border-emerald-200 bg-emerald-50 text-emerald-700" : "border border-rose-200 bg-rose-50 text-rose-700"}`}
                    >
                      {stat.trend === "up" ? (
                        <ArrowUpRight className="mr-0.5 h-3 w-3" />
                      ) : (
                        <ArrowDownRight className="mr-0.5 h-3 w-3" />
                      )}
                      {stat.change}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-stone-900">
                      {stat.value}
                    </p>
                    <p className="mt-1 text-xs font-medium text-stone-500">
                      {stat.title}
                    </p>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          {/* Quick Actions */}
          <Card className="shadow-xs rounded-2xl border border-stone-200/80 bg-white">
            <CardHeader className="border-b border-stone-100 pb-3">
              <CardTitle className="font-display text-base font-bold text-stone-900">
                Quick Actions
              </CardTitle>
              <CardDescription className="text-xs text-stone-500">
                Frequently used operations
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-2.5 pt-4">
              {quickActions.map(action => {
                const Icon = action.icon;
                return (
                  <Link key={action.label} to={action.href}>
                    <Button
                      variant="outline"
                      className="h-11 w-full justify-start rounded-xl border-stone-200 text-stone-700 transition-all hover:border-amber-400 hover:bg-amber-50/50"
                    >
                      <div className="mr-3 flex h-7 w-7 items-center justify-center rounded-lg bg-amber-100 text-amber-800">
                        <Icon className="h-3.5 w-3.5" />
                      </div>
                      <span className="text-sm font-semibold">
                        {action.label}
                      </span>
                    </Button>
                  </Link>
                );
              })}
            </CardContent>
          </Card>

          {/* Charts Section */}
          <Card className="shadow-xs rounded-2xl border border-stone-200/80 bg-white lg:col-span-2">
            <CardHeader className="flex flex-row items-center justify-between border-b border-stone-100 pb-3">
              <div>
                <CardTitle className="font-display text-base font-bold text-stone-900">
                  LMS Activity Monitor
                </CardTitle>
                <CardDescription className="text-xs text-stone-500">
                  Book circulation & manuscript returns
                </CardDescription>
              </div>
              <BarChart3 className="h-5 w-5 text-amber-600" />
            </CardHeader>
            <CardContent className="pt-4">
              <div className="h-[240px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={chartData}
                    margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      vertical={false}
                      stroke="#f1f5f9"
                    />
                    <XAxis
                      dataKey="name"
                      tickLine={false}
                      axisLine={false}
                      tick={{ fill: "#64748b", fontSize: 12 }}
                    />
                    <YAxis
                      tickLine={false}
                      axisLine={false}
                      tick={{ fill: "#64748b", fontSize: 12 }}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "#ffffff",
                        border: "1px solid #e2e8f0",
                        borderRadius: "12px",
                        boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.05)",
                      }}
                      labelStyle={{ fontWeight: "bold", color: "#0f172a" }}
                    />
                    <Legend
                      verticalAlign="top"
                      height={36}
                      iconType="circle"
                      iconSize={8}
                      wrapperStyle={{ fontSize: 12, fontWeight: 500 }}
                    />
                    <Bar
                      name="Issued"
                      dataKey="issued"
                      fill="#d97706"
                      radius={[4, 4, 0, 0]}
                    />
                    <Bar
                      name="Returned"
                      dataKey="returned"
                      fill="#059669"
                      radius={[4, 4, 0, 0]}
                    />
                    <Bar
                      name="Overdue"
                      dataKey="overdue"
                      fill="#e11d48"
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Mini Stats */}
              <div className="mt-4 grid grid-cols-3 gap-4">
                <div className="rounded-xl border border-amber-100 bg-amber-50/50 p-3 text-center">
                  <p className="text-lg font-bold text-amber-900">248</p>
                  <p className="text-xs font-medium text-amber-700">Issued</p>
                </div>
                <div className="rounded-xl border border-emerald-100 bg-emerald-50/50 p-3 text-center">
                  <p className="text-lg font-bold text-emerald-900">192</p>
                  <p className="text-xs font-medium text-emerald-700">
                    Returned
                  </p>
                </div>
                <div className="rounded-xl border border-rose-100 bg-rose-50/50 p-3 text-center">
                  <p className="text-lg font-bold text-rose-900">56</p>
                  <p className="text-xs font-medium text-rose-700">Overdue</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Bottom Section */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Recent Activities */}
          <Card className="shadow-xs rounded-2xl border border-stone-200/80 bg-white">
            <CardHeader className="border-b border-stone-100 pb-3">
              <CardTitle className="font-display text-base font-bold text-stone-900">
                Recent Activities
              </CardTitle>
              <CardDescription className="text-xs text-stone-500">
                Latest actions across the ecosystem
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-4">
              <div className="space-y-3">
                {recentActivities.map((activity, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-3.5 rounded-xl border border-transparent p-2.5 transition-colors hover:border-amber-100 hover:bg-amber-50/40"
                  >
                    <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-amber-100/60 text-amber-800">
                      <Activity className="h-4 w-4 text-amber-700" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold text-stone-900">
                        {activity.action}
                      </p>
                      <p className="text-xs text-stone-500">{activity.user}</p>
                    </div>
                    <span className="whitespace-nowrap rounded-full bg-stone-100 px-2 py-0.5 text-xs font-medium text-stone-400">
                      {activity.time}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* System Status */}
          <Card className="shadow-xs rounded-2xl border border-stone-200/80 bg-white">
            <CardHeader className="border-b border-stone-100 pb-3">
              <CardTitle className="font-display text-base font-bold text-stone-900">
                System Status
              </CardTitle>
              <CardDescription className="text-xs text-stone-500">
                Index engine and storage indicators
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5 pt-4">
              <div>
                <div className="mb-1.5 flex items-center justify-between">
                  <span className="text-xs font-semibold text-stone-700">
                    PDF Search Index
                  </span>
                  <span className="text-xs font-bold text-amber-700">78%</span>
                </div>
                <Progress
                  value={78}
                  className="h-2 rounded-full bg-stone-100 [&>div]:bg-amber-500"
                />
              </div>
              <div>
                <div className="mb-1.5 flex items-center justify-between">
                  <span className="text-xs font-semibold text-stone-700">
                    Storage Used
                  </span>
                  <span className="text-xs font-bold text-emerald-700">
                    45%
                  </span>
                </div>
                <Progress
                  value={45}
                  className="h-2 rounded-full bg-stone-100 [&>div]:bg-emerald-500"
                />
              </div>
              <div>
                <div className="mb-1.5 flex items-center justify-between">
                  <span className="text-xs font-semibold text-stone-700">
                    Research Entries
                  </span>
                  <span className="text-xs font-bold text-amber-800">92%</span>
                </div>
                <Progress
                  value={92}
                  className="h-2 rounded-full bg-stone-100 [&>div]:bg-amber-600"
                />
              </div>
              <div className="flex items-center justify-between border-t border-stone-100 pt-2">
                <div>
                  <span className="text-xs font-semibold text-stone-700">
                    AI Processing Pipeline
                  </span>
                  <p className="text-[11px] text-stone-400">
                    OCR & Transliteration active
                  </p>
                </div>
                <Badge className="border border-emerald-200 bg-emerald-50 text-xs font-semibold text-emerald-700">
                  Online
                </Badge>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
}
